import type { ApiClient } from "./client";

/**
 * Thread-based conversational API for BriefTaker.
 *
 * The backend is NOT a REST "briefs" resource — it is a LangGraph conversation
 * keyed by a client-generated `thread_id`. The first POST /chat/{thread_id}
 * implicitly creates the thread; subsequent /upload and validate/generate calls
 * read state that only exists once /chat has run for that thread.
 *
 * Endpoints (see brieftaker-app-api/back/main.py):
 *  - POST /chat/{thread_id}            body {user_input}          (scope brieftaker.read)
 *  - POST /upload/{thread_id}          multipart files[]          (scope brieftaker.write)
 *  - POST /order_attributes/{thread_id}                           (scope brieftaker.write)
 *
 * Business errors are returned with HTTP 200 and `status: "error"` — callers
 * must inspect `status`, not just the HTTP code.
 */

/** Sentinel sent as `user_input` to auto-start collection on a fresh thread. */
export const START_COLLECTION = "COMMENCER_COLLECTE_BRIEF_IMMEDIAT";
/** Sentinel sent as `user_input` to trigger final generation. */
export const VALIDATE_AND_GENERATE = "VALIDATE_AND_GENERATE";

/** Metadata driving which input widget to render for the current question. */
export interface LastQuestionDict {
  /** Predefined answers (renders pills / combobox). */
  options?: string[];
  /** Selection mode for `options`. */
  mode?: "one" | "multi" | "dropdown";
  /** Special widget: "date" → date picker, "image" → upload panel. */
  type?: "date" | "image" | string;
  /** Brief-data attribute(s) this question fills. */
  attributs?: string[];
}

/** Accumulated structured brief — ~120 optional fields filled progressively. */
export type BriefData = Record<string, unknown>;

export interface ChatResponse {
  response: string;
  next_node?: string | null;
  status?: string; // collecting | awaiting_validation_1 | awaiting_validation | finished | error
  brief_data?: BriefData | null;
  thread_id: string;
  last_question?: string | null;
  last_question_id?: number | string | null;
  last_question_dictionnary?: LastQuestionDict | null;
  // Present only on the finished (generation) response:
  document_url?: string | null;
  centre_folder?: string;
  brief_folder?: string;
  final_folder_prefix?: string;
}

export interface UploadedItem {
  file_name: string;
  url?: string;
  error?: string;
  [key: string]: unknown;
}

export interface UploadResponse {
  uploaded: UploadedItem[];
  thread_id: string;
  uploaded_count: number;
  /** Set when /chat has not populated state yet (uploads rejected). */
  error?: string;
  centre_folder?: string;
  brief_folder?: string;
  final_folder_prefix?: string;
  brief_datetime?: string;
}

const enc = encodeURIComponent;

export const chatApi = {
  /** Send a user turn (or a sentinel) to the conversation. */
  send(client: ApiClient, threadId: string, userInput: string): Promise<ChatResponse> {
    return client.request<ChatResponse>(`/chat/${enc(threadId)}`, {
      method: "POST",
      body: JSON.stringify({ user_input: userInput }),
    });
  },

  /** Upload staged files to the thread (multipart, field name "files"). */
  uploadFiles(client: ApiClient, threadId: string, files: File[]): Promise<UploadResponse> {
    const form = new FormData();
    for (const f of files) form.append("files", f, f.name);
    return client.request<UploadResponse>(`/upload/${enc(threadId)}`, {
      method: "POST",
      body: form,
    });
  },

  /** Canonical attribute display order (backend-driven). */
  orderAttributes(client: ApiClient, threadId: string): Promise<string[]> {
    return client
      .request<{ ordered_attributes?: string[] }>(`/order_attributes/${enc(threadId)}`, {
        method: "POST",
      })
      .then((r) => r.ordered_attributes ?? []);
  },
};

/**
 * Mirror of the Streamlit `_is_collection_complete`: decides when to flip the
 * UI into the "validate & generate" gate.
 */
const VALIDATION_PHRASES = [
  "peux-tu valider le brief",
  "valider le brief",
  "toutes les informations",
  "prêt à générer",
  "valide le brief",
];

export function isCollectionComplete(res: ChatResponse): boolean {
  const status = res.status ?? "";
  if (status === "awaiting_validation_1" || status === "awaiting_validation") return true;
  if (res.next_node === "db_writer") return true;
  const text = (res.response ?? "").toLowerCase();
  return VALIDATION_PHRASES.some((p) => text.includes(p));
}
